import { Link } from "react-router-dom";
import { IDynamicFormElement, ISelectFieldSingleItem } from "../../components/Form/DynamicForm";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";
import BaseApp, { IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig } from "../BaseApp";
import BrandsApp from "../brands/BrandsApp";
import { addNewBrand, useReturnAllBrands } from "../brands/brandsSlice";
import SectionsApp from "../sections/SectionsApp";
import { addNewSection, updateSection, useReturnAllSections } from "../sections/sectionsSlice";
import WarehousesApp from "../warehouses/WarehousesApp";
import { addNewWarehouse, updateWarehouse, useReturnAllWarehouses } from "../warehouses/warehousesSlice";
import { addNewItem, deleteItem, updateAllItems, updateItem, updateItemsLoadingStatus, updateItemsPagination, updateItemsSearch, useReturnAllItems, useReturnItemsLoadingStatus, useReturnItemsPagination, useReturnItemsSearch } from "./itemsSlice";
import ShowContainedItemsButton from "./ShowContainedItemsButton";
import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import ViewSingleItemPage from "./ViewSingleItemPage";
import { JSX } from "react/jsx-runtime";
import QuickSingleItemPreview from "./QuickSingleItemPreview";
import { IAxiosDefaultDataObject } from "../../config";
import { IAttachment } from "../../components/Attachments";

/**
 * Item Model
 */
export interface IItemModel{
    id: number,
    condition: string,
    is_container: boolean,
    parent_item: number | null,
    status: string,
    title: string,
    slug: string,
    description: string,
    serial_number: string,
    thumbnail: string,
    unique_id: string,
    warehouse_id: number,
    section_id: number,
    category_id: number,
    created_by: number,
    date_created: string,
    last_updated: string,
    is_active: false,
    warehouse_title: string,
    section_title: string,
    category_title: string,
    created_by_name: string,
    location_id: number,
    location_title: string,
    user_id: number,
    user_full_name: string,
    brand_id: number,
    brand_title: string,
    quantity: number,
    weight: number | null,
    parent_category_title: string,
    inventory_details?: IInventoryModel,
    inventory_count?: number,
    damaged: number,
    missing: number,
    reserved: number,
    item_model: string,
    watts: number,
    available_quantity: number,
    additional_data: IAxiosDefaultDataObject,
    images: IAttachment[]
}

export const EmptyItemModel: IItemModel = {
    id: 0,
    status: "",
    condition: "",
    is_container: false,
    parent_item: null,
    title: "",
    slug: "",
    description: "",
    serial_number: "",
    thumbnail: "",
    unique_id: "",
    warehouse_id: 0,
    section_id: 0,
    category_id: 0,
    created_by: 0,
    date_created: "",
    last_updated: "",
    is_active: false,
    warehouse_title: "",
    section_title: "",
    category_title: "",
    created_by_name: "",
    location_id: 0,
    location_title: "",
    user_id: 0,
    user_full_name: "",
    brand_id: 0,
    brand_title: "",
    quantity: 0,
    weight: null,
    parent_category_title: "",
    damaged: 0,
    missing: 0,
    reserved: 0,
    item_model: "",
    watts: 0,
    available_quantity: 0,
    additional_data: {},
    images: []
}

/**
 * Item Status List
 */
export interface ISingleItemStatusItem {
    type: string,
    label: string
}

/**
 * Export Item Types
 */
export type ItemsExportType = "selected_items" | "warehouse_items" | "section_items"

/**
 * Inventory Model
 */
export interface IInventoryModel {
    id: number,
    item: number,
    warehouse_id: number,
    section_id: number,
    location_id: number,
    user_id: number,
    quantity: number,
    warehouse: string,
    section: string,
    location: string,
    user: string,
    thumbnail: string,
    reserved_quantity: number
}

interface ContainerItemResponse {
    data: IItemModel[]
}

/**
 * Item types
 */
export type ItemsTypes = "regular" | "container"

export const itemsGridDataColumns: GridDataColumns = [
    {
        field: "title",
        headerName: "Item",
        type: "title",
        width: 350,
        minWidth: 350,
        cellRendererParams: {
            appClass: () => new ItemsApp(),
            enableQuickPreview: true,
        }
    },
];

/**
 * Item Additional Category Data
 */
export interface IItemAdditionalCategoryData {
    tv_screens: string[],
    media_storage: string[],
    furniture: string[],
}

/**
 * Sections App
 */
export default class ItemsApp extends BaseApp{

    appName = "items"
    appBaseEndPoint = "/items"

    // customListEndPoint: string = this.appBaseEndPoint

    elemPrefix: string = "items_"

    messages = {
        create: "Create a new transaction",
        createShort: "New transaction",
        edit: "Edit transaction",
        editShort: "Edit",
        delete: "Delete transaction",
        deleteShort: "Delete",
        view: "View transaction",
        nothingFound: "No items have been found.",
    }

    /**
     * Status
     */
    statusList: Array<ISingleItemStatusItem> = [
        {
            type: "on-going",
            label: "On-going"
        },
        {
            type: "resolved",
            label: "Resolved"
        },
        {
            type: "available",
            label: "Available"
        },
        {
            type: "in-use",
            label: "Taken"
        },
        {
            type: "missing",
            label: "Missing"
        },
        {
            type: "damaged",
            label: "Damaged"
        },
        {
            type: "taken",
            label: "Taken"
        },
        {
            type: "returned",
            label: "Returned"
        },
        {
            type: "transferred",
            label: "Transferred"
        },
        {
            type: "to-warehouse",
            label: "New Warehouse"
        },
        {
            type: "to-section",
            label: "New Section"
        },
        {
            type: "to-location",
            label: "New Location"
        }
    ]

    /**
     * Status
     */
    itemStatusList: Array<ISelectFieldSingleItem> = [
        {
            value: "available",
            name: "Available"
        },
        {
            value: "unavailable",
            name: "Unavailable"
        },
        {
            value: "reserved",
            name: "Reserved"
        },
        {
            value: "missing",
            name: "Missing"
        },
    ]

    itemConditions: Array<ISelectFieldSingleItem> = [
        {
            value: "good",
            name: "Good"
        },
        {
            value: "damaged",
            name: "Damaged"
        },
    ]

    screenResolutions: ISelectFieldSingleItem[] = [
        {
            value: "hd",
            name: "720p (HD): 1280x720px"
        },
        {
            value: "fullhd",
            name: "1080p (Full HD): 1920x1080px."
        },
        {
            value: "4k",
            name: "4K UHD: 3840x2160px"
        },
        {
            value: "8k",
            name: "8K UHD: 7680x4320px"
        },
    ]

    mediaStorageType: ISelectFieldSingleItem[] = [
        {
            value: "sdd",
            name: "SSD"
        },
        {
            value: "hdd",
            name: "HDD"
        },
        {
            value: "sd_card",
            name: "SD Card"
        },
        {
            value: "cf_card",
            name: "CF Card"
        },
        {
            value: "microsd",
            name: "MicroSD"
        },
        {
            value: "xqd",
            name: "XQD"
        },
        {
            value: "cfast",
            name: "CFast"
        },
        {
            value: "nvme",
            name: "NVMe"
        },
        {
            value: "other",
            name: "Other"
        },
    ]

    materialTypes: ISelectFieldSingleItem[] = [
        { value: "wood", name: "Wood" },
        { value: "engineered_wood", name: "Engineered Wood" },
        { value: "metal", name: "Metal" },
        { value: "plastic", name: "Plastic" },
        { value: "glass", name: "Glass" },
        { value: "fabric", name: "Fabric" },
        { value: "leather", name: "Leather" },
        { value: "rattan_wicker", name: "Rattan / Wicker" },
        { value: "foam", name: "Foam" },
        { value: "stone", name: "Stone" },
        { value: "concrete", name: "Concrete" },
        { value: "bamboo", name: "Bamboo" },
        { value: "ceramic", name: "Ceramic" },
        { value: "paper_cardboard", name: "Paper / Cardboard" },
        { value: "mixed", name: "Mixed Materials" },
    ]

    furnitureColors: ISelectFieldSingleItem[] = [
        { value: "black", name: "Black" },
        { value: "white", name: "White" },
        { value: "gray", name: "Gray" },
        { value: "beige", name: "Beige" },
        { value: "brown", name: "Brown" },
        { value: "natural_wood", name: "Natural Wood" },
        { value: "dark_wood", name: "Dark Wood" },
        { value: "light_wood", name: "Light Wood" },
        
        { value: "blue", name: "Blue" },
        { value: "navy", name: "Navy" },
        { value: "green", name: "Green" },
        { value: "olive", name: "Olive" },
        { value: "red", name: "Red" },
        { value: "orange", name: "Orange" },
        { value: "yellow", name: "Yellow" },
        { value: "pink", name: "Pink" },
        
        { value: "gold", name: "Gold" },
        { value: "silver", name: "Silver" },
        { value: "bronze", name: "Bronze" },
        { value: "transparent", name: "Transparent" },
        { value: "multi_color", name: "Multi-color" }
    ]

    mountingTypes: ISelectFieldSingleItem[] = [
        { value: "freestanding", name: "Freestanding" },
        { value: "wall_mounted", name: "Wall-mounted" },
        { value: "ceiling_mounted", name: "Ceiling-mounted" },
        { value: "floor_mounted", name: "Floor-mounted" },
        { value: "desk_mounted", name: "Desk-mounted" },
        { value: "rack_mounted", name: "Rack-mounted" },
        { value: "rail_mounted", name: "Rail-mounted" },
        { value: "pole_mounted", name: "Pole-mounted" },
        { value: "truss_mounted", name: "Truss-mounted" },
        { value: "clamp_mounted", name: "Clamp-mounted" },
        { value: "flush_mounted", name: "Flush-mounted" },
        { value: "magnetic", name: "Magnetic" },
        { value: "adhesive", name: "Adhesive / Taped" },
        { value: "hook_hung", name: "Hook-hung" },
        { value: "embedded", name: "Embedded / Recessed" },
        { value: "portable", name: "Portable / Not Mounted" }
    ]

    furnitureFeatures: ISelectFieldSingleItem[] = [
        { value: "stackable", name: "Stackable" },
        { value: "foldable", name: "Foldable" },
        { value: "extendable", name: "Extendable" },
        { value: "modular", name: "Modular" },
        { value: "collapsible", name: "Collapsible" },
        { value: "adjustable_height", name: "Adjustable Height" },
        { value: "adjustable_tilt", name: "Adjustable Tilt" },
        { value: "reclining", name: "Reclining" },
        { value: "rotating", name: "Rotating / Swivel" },
        { value: "with_wheels", name: "With Wheels / Casters" },
        { value: "lockable_wheels", name: "Lockable Wheels" },
        { value: "with_armrests", name: "With Armrests" },
        { value: "with_backrest", name: "With Backrest" },
        { value: "with_storage", name: "With Storage" },
        { value: "cable_management", name: "Cable Management" },
        { value: "folding_top", name: "Folding Top" },
        { value: "detachable_parts", name: "Detachable Parts" },
        { value: "quick_assembly", name: "Quick Assembly" },
        { value: "tool_free_assembly", name: "Tool-free Assembly" },
        { value: "lightweight", name: "Lightweight" },
        { value: "heavy_duty", name: "Heavy-duty" },
        { value: "indoor_use", name: "Indoor Use" },
        { value: "outdoor_use", name: "Outdoor Use" },
        { value: "weather_resistant", name: "Weather Resistant" },
        { value: "fire_retardant", name: "Fire Retardant" },
        { value: "waterproof", name: "Waterproof" },
        { value: "acoustic", name: "Acoustic Properties" },
        { value: "smart_features", name: "Smart Features (Power / USB / Lighting)" }
    ]

    /**
     * Additional Data Categories List
     */
    additionalCategoriesDataList: IItemAdditionalCategoryData = {
        tv_screens: [
            "tv",
        ],
        media_storage: [
            "media storage",
            "ssd",
            "hdd",
            "memory cards",
            "readers",
            "nas storage",
        ],
        furniture: [
            "furniture",
            "chairs",
            "sofas",
            "benches",
            "stools",
            "readers",
            "ottoman",
            "tables",
            "desks",
        ]
    }

    /**
     * Return screen resolution name
     */
    returnScreenResolutionName = (value: string) => {
        const findResolution = this.screenResolutions.find((res) => res.value === value)

        if (findResolution){
            return findResolution.name
        }

        return ""
    }

    /**
     * Return generic select name
     */
    returnGenericSelectNameByValue = (value: string, item: ISelectFieldSingleItem[]) => {
        const find = item.find((res) => res.value === value)

        if (find){
            return find.name
        }

        return ""
    }

    quickPreview = (id: number) => {
        return <QuickSingleItemPreview itemId={id} />
    }

    /**
     * Dynamic Base Page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllItems,
        pagination: useReturnItemsPagination,
        loading: useReturnItemsLoadingStatus,
        search: useReturnItemsSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateItemsLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteItem({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewItem((item as IItemModel))),
        updateItem: (item, dispatch) => dispatch(updateItem((item as IItemModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllItems((items as IItemModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateItemsSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateItemsPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/brands/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/brands/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
        // filterForm: [],
        // customHeaderOptionActionButtons: [],
        // viewItemTemplateLink: "",
        // disableAutoViewItemLink: false,
        // hidePageHeadSection: false,
        // customTitlePageHeadSection: "",
        // disableSearch: false,
        // customPageHeadSectionActionButtons: []
    }

    // useGridData: boolean = true
    // gridConfig: IGridDataConfig = {
    //     viewItemLink: "",
    //     editItemLink: "",
    //     filterForm: {
    //         name: "select_items_filter_form",
    //         elements: [
    //             {
    //                 id: "status",
    //                 xs: 4,
    //                 type: "Select",
    //                 label: "Status",
    //                 fullWidth: true,
    //                 addNoneItem: true,
    //                 items: new ItemsApp().itemStatusList
    //             },
    //             {
    //                 id: "warehouse_id",
    //                 xs: 4,
    //                 type: "DynamicModelSelect",
    //                 label: "Warehouse",
    //                 fullWidth: true,
    //                 appClass: new WarehousesApp(),
    //                 selectLabel: "Select Warehouse",
    //                 gridColumns: new WarehousesApp().gridDataColumns
    //             },
    //             {
    //                 id: "section_id",
    //                 xs: 4,
    //                 type: "DynamicModelSelect",
    //                 label: "Section",
    //                 fullWidth: true,
    //                 appClass: new SectionsApp(),
    //                 selectLabel: "Select Section",
    //                 gridColumns: new SectionsApp().gridDataColumns,
    //                 filterForm: new SectionsApp().gridConfig.filterForm
    //             },
    //             {
    //                 id: "category_id",
    //                 xs: 6,
    //                 type: "SelectCategory",
    //                 label: "Category",
    //                 fullWidth: true,
    //                 displayForItem: true,
    //                 addNoneItem: true,
    //             },
    //             {
    //                 id: "brand_id",
    //                 xs: 6,
    //                 type: "DynamicModelSelect",
    //                 label: "Brand",
    //                 fullWidth: true,
    //                 appClass: new BrandsApp(),
    //                 selectLabel: "Select Brand",
    //                 gridColumns: new BrandsApp().gridDataColumns,
    //                 filterForm: new BrandsApp().gridConfig.filterForm
    //             },
    //         ]
    //     }
    // }

    /**
     * Return status label
     */
    returnStatusLabel = (type: string) => {

        let label: string = ""

        this.statusList.map((status) => {
            if (status.type === type){
                label = status.label
            }
        })

        return label

    }

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "thumbnail",
                type: "thumbnail",
                label: "Thumbnail"
            },
            {
                id: "status",
                type: "status",
                label: "Status",
                maxWidth: 150,
                minWidth: 150,
            },
            {
                id: "is_container",
                type: "status",
                label: "Type",
                maxWidth: 150,
                minWidth: 150,
                customContent(item) {
                    return <StatusChip type={item.is_container ? "container" : "regular"} />
                },
            },
            {
                id: "title",
                type: "text",
                label: "Title",
                maxWidth: 200,
                minWidth: 200,
                customContent(item) {
                    
                    return(
                        <>
                            <Link to={`/panel/items/view/${item.id}`}>
                                <h3 className="title">{item.title}</h3>
                            </Link>

                            {item.is_container && <ShowContainedItemsButton item={(item as IItemModel)} />}
                        </>
                    )
                },
            },
            {
                id: "warehouse_title",
                type: "current-location",
                label: "Current Location",
                maxWidth: 350,
                minWidth: 350,
            },
            {
                id: "brand_title",
                type: "text",
                label: "Brand",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "category_title",
                type: "text",
                label: "Category",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "created_by_name",
                type: "text",
                label: "Created by",
                maxWidth: 250,
                minWidth: 250,
            },
            {
                id: "quantity",
                type: "text",
                label: "Quantity",
                maxWidth: 100,
                minWidth: 100,
            },
            {
                id: "unique_id",
                type: "text",
                label: "Unique ID",
                maxWidth: 250,
                minWidth: 250,
            },
            {
                id: "date_created",
                type: "date",
                label: "Date Created",
                maxWidth: 300,
                minWidth: 300,
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No items have been found :("
        }
    }

    // quickPreview = (id: number) => {
    //     return <QuickSingleItemPreview itemId={id} />
    // }

    // formConfig: IBaseAppFormConfig = {
    //     form: {
    //         name: "items_form",
    //         elements: [
    //             {
    //                 id: this.elemPrefix + "title",
    //                 xs: 12,
    //                 type: "TextField",
    //                 label: "Title",
    //                 fullWidth: true
    //             },
    //             {
    //                 id: this.elemPrefix + "warehouse_id",
    //                 xs: 3,
    //                 type: "DynamicSelect",
    //                 label: "Waerehouse",
    //                 fullWidth: true,
    //                 itemsList: useReturnAllWarehouses,
    //                 modelTitleParam: "title",
    //                 appClass: new WarehousesApp(),
    //                 addNewItem: addNewWarehouse,
    //             },
    //             {
    //                 id: this.elemPrefix + "section_id",
    //                 xs: 3,
    //                 type: "DynamicSelect",
    //                 label: "Section",
    //                 fullWidth: true,
    //                 itemsList: useReturnAllSections,
    //                 modelTitleParam: "title",
    //                 appClass: new SectionsApp(),
    //                 addNewItem: addNewSection,
    //             },
    //             {
    //                 id: this.elemPrefix + "category_id",
    //                 xs: 3,
    //                 type: "SelectCategory",
    //                 label: "Category",
    //                 fullWidth: true,
    //                 displayForItem: true
    //             },
    //             {
    //                 id: this.elemPrefix + "brand_id",
    //                 xs: 3,
    //                 type: "DynamicSelect",
    //                 label: "Brand",
    //                 fullWidth: true,
    //                 itemsList: useReturnAllBrands,
    //                 modelTitleParam: "title",
    //                 appClass: new BrandsApp(),
    //                 addNewItem: addNewBrand,
    //             },
    //             {
    //                 id: this.elemPrefix + "description",
    //                 xs: 12,
    //                 type: "TextField",
    //                 label: "Description",
    //                 fullWidth: true,
    //                 multiline: true,
    //                 maxRows: 4,
    //                 minRows: 4
    //             },
    //             {
    //                 id: this.elemPrefix + "serial_number",
    //                 xs: 6,
    //                 type: "TextField",
    //                 label: "Serial Number",
    //                 fullWidth: true
    //             },
    //             {
    //                 id: this.elemPrefix + "unique_id",
    //                 xs: 6,
    //                 type: "TextField",
    //                 label: "Custom Unique ID",
    //                 fullWidth: true,
    //                 enableQrCodeScanner: true,
    //                 validateInput: (text: string) => this.validateUniqueId(text),
    //                 placeholder: "01D23DAE-0384-43DB-A"
    //             },
    //             {
    //                 id: this.elemPrefix + "thumbnail",
    //                 xs: 12,
    //                 type: "ImageUploader",
    //                 label: "Thumbnail",
    //                 fullWidth: true
    //             },
    //         ]
    //     },
    //     sectionBox: {
    //         showTitle: true,
    //         title: "Details",
    //         description: "Fill in the item details"
    //     },
    //     requiredFields: [
    //         {
    //             id: this.elemPrefix + "title",
    //             message: "Title is required!"
    //         },
    //         {
    //             id: this.elemPrefix + "warehouse_id",
    //             message: "Warehouse is required!"
    //         },
    //         {
    //             id: this.elemPrefix + "section_id",
    //             message: "Section is required!"
    //         },
    //         {
    //             id: this.elemPrefix + "category_id",
    //             message: "Category is required!"
    //         },
    //         {
    //             id: this.elemPrefix + "unique_id",
    //             message: "Invalid format!",
    //             customValidate: (val) => {
    //                 if (val){
    //                     return this.validateUniqueId(val)
    //                 }

    //                 return true
    //             }
    //         },
    //     ]
    // }

    // quickPreview = (id: number) => {
    //     if (!id){
    //         return <></>
    //     }   

    //     return <ViewSingleItemPage customId={id} />
    // }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "items_form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 12,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "category_id",
                    xs: 6,
                    type: "SelectCategory",
                    label: "Category",
                    fullWidth: true,
                    displayForItem: true
                },
                {
                    id: this.elemPrefix + "brand_id",
                    xs: 6,
                    type: "DynamicModelSelect",
                    label: "Brand",
                    fullWidth: true,
                    appClass: new BrandsApp(),
                    selectLabel: "Select Brand",
                    gridColumns: new BrandsApp().gridDataColumns
                },
                {
                    id: this.elemPrefix + "description",
                    xs: 12,
                    type: "TextField",
                    label: "Description",
                    fullWidth: true,
                    multiline: true,
                    maxRows: 4,
                    minRows: 4
                },
                {
                    id: this.elemPrefix + "serial_number",
                    xs: 12,
                    type: "TextField",
                    label: "Serial Number",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "item_model",
                    xs: 12,
                    type: "TextField",
                    label: "Model",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "weight",
                    xs: 6,
                    type: "TextField",
                    label: "Weight (kg)",
                    fullWidth: true,
                    fieldType: "Number",
                },
                {
                    id: this.elemPrefix + "watts",
                    xs: 6,
                    type: "TextField",
                    label: "Power Output (Watts)",
                    fullWidth: true,
                    fieldType: "Number",
                },
                {
                    id: this.elemPrefix + "images",
                    xs: 12,
                    type: "Images",
                    label: "Images",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the item details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
            {
                id: this.elemPrefix + "category_id",
                message: "Category is required!"
            },
            {
                id: this.elemPrefix + "brand_id",
                message: "Brand is required!"
            },
        ]
    }

    gridDataColumns: GridDataColumns = itemsGridDataColumns

    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/items/view/[unique_id]",
        publicViewItemLink: "/items/[unique_id]",
        editItemLink: "/panel/items/edit/[id]",
        hideFilters: true,
    }

    itemsListFilterFormDefaultElements: IDynamicFormElement[] = [
        {
                id: "status",
                xs: 4,
                type: "Select",
                label: "Status",
                fullWidth: true,
                addNoneItem: true,
                items: this.itemStatusList
            },
            {
                id: "condition",
                xs: 4,
                type: "Select",
                label: "Condition",
                fullWidth: true,
                addNoneItem: true,
                items: this.itemConditions
            },
            {
                id: "category_id",
                xs: 4,
                type: "SelectCategory",
                label: "Category",
                fullWidth: true,
                displayForItem: true,
                addNoneItem: true,
            },
            {
                id: "warehouse_id",
                xs: 4,
                type: "DynamicModelSelect",
                label: "Warehouse",
                fullWidth: true,
                appClass: new WarehousesApp(),
                selectLabel: "Select Warehouse",
                gridColumns: new WarehousesApp().gridDataColumns
            },
            {
                id: "section_id",
                xs: 4,
                type: "DynamicModelSelect",
                label: "Section",
                fullWidth: true,
                appClass: new SectionsApp(),
                selectLabel: "Select Section",
                gridColumns: new SectionsApp().gridDataColumns,
                filterForm: new SectionsApp().gridConfig.filterForm
            },
            {
                id: "brand_id",
                xs: 4,
                type: "DynamicModelSelect",
                label: "Brand",
                fullWidth: true,
                appClass: new BrandsApp(),
                selectLabel: "Select Brand",
                gridColumns: new BrandsApp().gridDataColumns,
                filterForm: new BrandsApp().gridConfig.filterForm
            },
    ]

    /**
     * Validate unique id
     */
    validateUniqueId = (unqiueId: string) =>{
        const regex = /^[A-Z0-9]{8}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]$/;
        return regex.test(unqiueId);
    }

    generateUniqueId = (): string  =>{
        const getRandomSegment = (length: number): string =>
          Array.from({ length }, () =>
            Math.floor(Math.random() * 16).toString(16).toUpperCase()
          ).join("");
      
        const part1: string = getRandomSegment(8); // 8 characters
        const part2: string = getRandomSegment(4); // 4 characters
        const part3: string = getRandomSegment(4); // 4 characters
        const part4: string = getRandomSegment(1); // 1 character
      
        return `${part1}-${part2}-${part3}-${part4}`;
    }

    /**
     * Single item details based by unique id
     */
    singleItemDetailsByUniqueId = async(uniqueId: string, usePublicDetails: boolean = false) => {

        if (usePublicDetails){
            return await api.get(this.appBaseEndPoint + "/public/" + uniqueId)
        }

        return await api.get(this.appBaseEndPoint + "/use-unique-id/" + uniqueId)
    }

    /**
     * Statistics
     */
    getStatistics = async() => {
        return await api.get(this.appBaseEndPoint + "/statistics" )
    }

    /**
     * Statistics
     */
    verifyItem = async(uniqueId: string) => {
        return await api.get(this.appBaseEndPoint + "/verify/" + uniqueId )
    }

    /**
     * Export Items
     */
    exportItems = async(export_type: ItemsExportType, items: Number[], warehouse_id: number = 0, section_id: number = 0) => {
        return await api.post(this.appBaseEndPoint + "/export", {
            export_type,
            items,
            warehouse_id,
            section_id
        }, { responseType: "blob" })
    }

    /**
     * Item current inventory
     */
    itemInventory = async(uniqueId: string, show_only_first: boolean = false) => {

        let link: string = "/current-inventory/" + uniqueId

        if (show_only_first){
            link = link + "?show_only_first=1"
        }

        return await api.get(this.appBaseEndPoint + link )
    }

    /**
     * Item available-quantity
     */
    itemAvailableQuantity = async(
        item_id: number, 
        warehouse_id: number | null, 
        section_id: number | null, 
        location_id: number | null, 
        user_id: number | null
    ) => {
        return await api.post(this.appBaseEndPoint + "/inventory/available-quantity", {
            item_id,
            warehouse_id,
            section_id,
            location_id,
            user_id,
        })
    }

    /**
     * New stock
     */
    addNewStockItem = async(
        item_id: number, 
        warehouse_id: number | null, 
        section_id: number | null, 
        location_id: number | null, 
        user_id: number | null,
        quantity: number,
        timestamp: string,
        note: string,
    ) => {
        return await api.post(this.appBaseEndPoint + "/inventory/new-stock", {
            item_id,
            warehouse_id,
            section_id,
            location_id,
            user_id,
            quantity,
            timestamp,
            note,
        })
    }

    /**
     * New stock
     */
    reduceStock = async(
        item_id: number, 
        warehouse_id: number | null, 
        section_id: number | null, 
        location_id: number | null, 
        user_id: number | null,
        quantity: number,
        timestamp: string,
        note: string,
    ) => {
        return await api.post(this.appBaseEndPoint + "/inventory/reduce-stock", {
            item_id,
            warehouse_id,
            section_id,
            location_id,
            user_id,
            quantity,
            timestamp,
            note,
        })
    }

    /**
     * Container list
     */
    returnContainerItemList = async (container_id: number): Promise<ContainerItemResponse> => {
        return await api.post(this.appBaseEndPoint + "/container/list", {
            container_id
        })
    }

    /**
     * Container list
     */
    newItemsToContainer = async(
        container_id: number,
        items: number[]
    ) => {
        return await api.post(this.appBaseEndPoint + "/container/new-items", {
            container_id,
            items,
        })
    }

    /**
     * Remove item from container
     */
    removeItemFromContainer = async(
        container_id: number,
        item_id: number,
    ) => {
        return await api.post(this.appBaseEndPoint + "/container/remove-item", {
            container_id,
            item_id
        })
    }

    /**
     * Update item submit data
     */
    updateItemSubmitData = (data: IAxiosDefaultDataObject, getElementDataValue: (id: string) => any, categoryTitle: string) => {

        let newData = {...data}

        categoryTitle = categoryTitle.toLocaleLowerCase()

        /**
         * Update data
         */
        if (categoryTitle){

            newData.category_title = categoryTitle

            /**
             * TV Category
             */
            if (this.additionalCategoriesDataList.tv_screens.some(word => categoryTitle.includes(word.toLowerCase()))){
                newData = {
                    ...newData,
                    screen_size: getElementDataValue("screen_size"),
                    resolution: getElementDataValue("resolution"),
                    screen_type: getElementDataValue("screen_type"),
                    refresh_rate: getElementDataValue("refresh_rate"),
                    hdmi_ports: getElementDataValue("hdmi_ports"),
                    smart_tv: getElementDataValue("smart_tv"),
                    os: getElementDataValue("os")
                }
            }

            /**
             * Media Storage
             */
            if (this.additionalCategoriesDataList.media_storage.some(word => categoryTitle.includes(word.toLowerCase()))){
                newData = {
                    ...newData,
                    storage_type: getElementDataValue("storage_type"),
                    capacity_gb: getElementDataValue("capacity_gb"),
                    read_speed: getElementDataValue("read_speed"),
                    write_speed: getElementDataValue("write_speed"),
                    is_encrypted: getElementDataValue("is_encrypted"),
                }
            }

            /**
             * Furniture
             */
            if (this.additionalCategoriesDataList.furniture.some(word => categoryTitle.includes(word.toLowerCase()))){
                newData = {
                    ...newData,
                    material: getElementDataValue("material"),
                    color: getElementDataValue("color"),
                    code: getElementDataValue("code"),
                    length_mm: getElementDataValue("length_mm"),
                    width_mm: getElementDataValue("width_mm"),
                    height_mm: getElementDataValue("height_mm"),
                    features: getElementDataValue("features"),
                    mounting_type: getElementDataValue("mounting_type"),
                }
            }

        }

        /**
         * Return updated data
         */
        return newData

    }

    /**
     * Serializer item additional data value
     */
    serializeItemAdditionalDataValue = (additionalData: {[key: string]: any}) => {

        let newAdditionalData = {...additionalData}

        /**
         * TV Screens
         */
        if ("screen_size" in newAdditionalData) {
            const screenSizeValue = newAdditionalData.screen_size ?? "";

            newAdditionalData.screen_size = screenSizeValue
                ? `${screenSizeValue} inch`
                : "";
        }

        if ("resolution" in newAdditionalData) {
            newAdditionalData.resolution = this.returnScreenResolutionName(newAdditionalData.resolution)
        }

        if ("refresh_rate" in newAdditionalData){
            const refreshRate = newAdditionalData.refresh_rate

            newAdditionalData.refresh_rate = refreshRate ? `${refreshRate} Hz` : ""
        }

        if ("smart_tv" in newAdditionalData){
            newAdditionalData.smart_tv = newAdditionalData.smart_tv ? "Yes" : "No"
        }

        /**
         * Media Storage
         */

        if ("storage_type" in newAdditionalData){
            if (newAdditionalData.storage_type){
                newAdditionalData.storage_type = this.returnGenericSelectNameByValue(newAdditionalData.storage_type, this.mediaStorageType)
            }
        }

        if ("capacity_gb" in newAdditionalData){
            newAdditionalData.capacity_gb = newAdditionalData.capacity_gb ? `${newAdditionalData.capacity_gb} GB` : ""
        }

        if ("read_speed" in newAdditionalData){
            newAdditionalData.read_speed = newAdditionalData.read_speed ? `${newAdditionalData.read_speed} MB/s` : ""
        }

        if ("write_speed" in newAdditionalData){
            newAdditionalData.write_speed = newAdditionalData.write_speed ? `${newAdditionalData.write_speed} MB/s` : ""
        }

        if ("is_encrypted" in newAdditionalData){
            newAdditionalData.is_encrypted = newAdditionalData.is_encrypted ? "Yes" : "No"
        }

        /**
         * Furniture
         */
        if ("color" in newAdditionalData){
            if (newAdditionalData.color){
                newAdditionalData.color = this.returnGenericSelectNameByValue(newAdditionalData.color, this.furnitureColors)
            }
        }

        if ("material" in newAdditionalData){
            if (newAdditionalData.material){
                newAdditionalData.material = this.returnGenericSelectNameByValue(newAdditionalData.material, this.materialTypes)
            }
        }

        if ("length_mm" in newAdditionalData){
            if (newAdditionalData.length_mm){
                newAdditionalData.length_mm = newAdditionalData.length_mm ? newAdditionalData.length_mm + "mm" : "-"
            }else{
                newAdditionalData.length_mm = "-"
            }
        }

        if ("width_mm" in newAdditionalData){
            if (newAdditionalData.width_mm){
                newAdditionalData.width_mm = newAdditionalData.width_mm ? newAdditionalData.width_mm + "mm" : "-"
            }else{
                newAdditionalData.width_mm = "-"
            }
        }

        if ("height_mm" in newAdditionalData){
            if (newAdditionalData.height_mm){
                newAdditionalData.height_mm = newAdditionalData.height_mm ? newAdditionalData.height_mm + "mm" : "-"
            }else{
                newAdditionalData.height_mm = "-"
            }
        }

        if ("features" in newAdditionalData){
            if (newAdditionalData.features){
                newAdditionalData.features = this.returnGenericSelectNameByValue(newAdditionalData.features, this.furnitureFeatures)
            }
        }

        if ("mounting_type" in newAdditionalData){
            if (newAdditionalData.mounting_type){
                newAdditionalData.mounting_type = this.returnGenericSelectNameByValue(newAdditionalData.mounting_type, this.mountingTypes)
            }
        }

        return newAdditionalData

    }

    /**
     * Serializer additional data keys
     */
    serializeItemAdditionaDataKeys = (additionalData: { [key: string]: any }) => {
        if (!additionalData){
            return {}
        }

        function normalizeKey(key: string) {
            return key
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/[^a-z0-9_]/g, ""); // Remove non-alphanumerics
        }

        const serialized: { [key: string]: any } = {};

        for (const [key, value] of Object.entries(additionalData)) {
            if (key === "fewer_details") continue;

            const normalizedKey = normalizeKey(key);
            serialized[normalizedKey] = value;
        }

        return serialized;
    };

    /**
     * Deserialize additional data keys
     */
    deserializeItemAdditionalDataKeys = (data: { [key: string]: any }) => {
        const deserialized: { [key: string]: any } = {};

        const forceUpper = [
            "tv", 
            "hdmi", 
            "os",
            "gb",
            "tb",
            "mm",
            "cm",
            "m",
        ];

        data = this.serializeItemAdditionalDataValue(data)

        for (const [key, value] of Object.entries(data)) {
            if (key === "fewer_details") continue;
            
            const readableKey = key
                .split("_")
                .map(word => {
                    return forceUpper.includes(word.toLowerCase())
                        ? word.toUpperCase()
                        : word.charAt(0).toUpperCase() + word.slice(1);
                })
                .join(" ");

            deserialized[readableKey] = value;
        }

        return deserialized;
    };

}